import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default empty form state — matches backend CustomerBase fields
const emptyForm = {
  name: '',
  code: '',
  email_addresses: '',             // delivery-note emails (legacy, comma-separated in UI)
  active: true,
  // ----- Invoicing -----
  invoice_email_to_addresses: '',  // TO recipients (one per line in UI)
  invoice_email_cc_addresses: '',  // CC recipients (one per line in UI)
  billing_contact_name: '',
  billing_contact_role: '',
  billing_address_lines: '',       // one address line per line in UI
  location_ref: '',                // number, 2 digits
  customer_ref: '',                // number, 2 digits
  invoice_workflow: 'direct',      // 'direct' | 'po_first'
  po_recipient_email: '',
  labelling_rate_ex_vat: '',       // number, e.g. 0.20
  // ----- Step 3c-ii: per-customer template overrides -----
  rate_decimal_places: 2,           // 2dp default; Ritz uses 3
  show_numbered_tablecloth_column: false,  // true only for O&C F&B
};

// Helpers to translate between backend (arrays, numbers, nulls) and form (strings)
const toLines = (arr) => (Array.isArray(arr) ? arr.join('\n') : (arr || ''));
const fromLines = (str) => (str || '').split('\n').map(s => s.trim()).filter(Boolean);
const toCsv = (arr) => (Array.isArray(arr) ? arr.join(', ') : (arr || ''));
const fromCsv = (str) => (str || '').split(',').map(s => s.trim()).filter(Boolean);
const numOrNull = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/operations/customers`);
      const data = await res.json();
      setCustomers(data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const url = editingCustomer
        ? `${API_URL}/api/operations/customers/${editingCustomer.id}`
        : `${API_URL}/api/operations/customers`;

      // Build the payload the backend expects
      const payload = {
        name: formData.name,
        code: formData.code,
        email_addresses: fromCsv(formData.email_addresses),
        active: formData.active,
        invoice_email_to_addresses: fromLines(formData.invoice_email_to_addresses),
        invoice_email_cc_addresses: fromLines(formData.invoice_email_cc_addresses),
        billing_contact_name: formData.billing_contact_name || null,
        billing_contact_role: formData.billing_contact_role || null,
        billing_address_lines: fromLines(formData.billing_address_lines),
        location_ref: numOrNull(formData.location_ref),
        customer_ref: numOrNull(formData.customer_ref),
        invoice_workflow: formData.invoice_workflow || 'direct',
        po_recipient_email: formData.po_recipient_email || null,
        labelling_rate_ex_vat: numOrNull(formData.labelling_rate_ex_vat),
        // Step 3c-ii: pack template_settings as a dict for the backend.
        template_settings: {
          rate_decimal_places: Number(formData.rate_decimal_places) || 2,
          show_numbered_tablecloth_column: !!formData.show_numbered_tablecloth_column,
        },
      };

      const res = await fetch(url, {
        method: editingCustomer ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to save customer');
      }

      await fetchCustomers();
      closeModal();
    } catch (error) {
      setError(error.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;

    try {
      await fetch(`${API_URL}/api/operations/customers/${id}`, { method: 'DELETE' });
      await fetchCustomers();
    } catch (error) {
      console.error('Error deleting customer:', error);
    }
  };

  const openModal = (customer = null) => {
    if (customer) {
      setEditingCustomer(customer);
      setFormData({
        name: customer.name || '',
        code: customer.code || '',
        email_addresses: toCsv(customer.email_addresses),
        active: customer.active !== false,
        invoice_email_to_addresses: toLines(customer.invoice_email_to_addresses),
        invoice_email_cc_addresses: toLines(customer.invoice_email_cc_addresses),
        billing_contact_name: customer.billing_contact_name || '',
        billing_contact_role: customer.billing_contact_role || '',
        billing_address_lines: toLines(customer.billing_address_lines),
        location_ref: customer.location_ref ?? '',
        customer_ref: customer.customer_ref ?? '',
        invoice_workflow: customer.invoice_workflow || 'direct',
        po_recipient_email: customer.po_recipient_email || '',
        labelling_rate_ex_vat: customer.labelling_rate_ex_vat ?? '',
        // Step 3c-ii: unpack template_settings dict into form fields. Falls
        // back to defaults when the dict is missing or sparse.
        rate_decimal_places: customer.template_settings?.rate_decimal_places ?? 2,
        show_numbered_tablecloth_column: !!customer.template_settings?.show_numbered_tablecloth_column,
      });
    } else {
      setEditingCustomer(null);
      setFormData(emptyForm);
    }
    setError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCustomer(null);
    setFormData(emptyForm);
    setError('');
  };

  const filteredCustomers = customers.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-lg text-gray-600">Loading...</div></div>;
  }

  const inputClass = "w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#1F2D3D] focus:border-transparent";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";
  const sectionHeaderClass = "text-sm font-semibold text-gray-800 uppercase tracking-wide border-b pb-1 mb-3 mt-2";
  const helpTextClass = "text-xs text-gray-500 mt-1";

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800" style={{ fontFamily: '"Cinzel", serif' }}>Customers</h1>
        <button onClick={() => openModal()} className="flex items-center px-4 py-2 bg-[#1F2D3D] text-white rounded-lg hover:bg-[#1A2332] transition-colors">
          <Plus size={20} className="mr-2" />Add Customer
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="relative">
          <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search customers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#1F2D3D] focus:border-transparent"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left py-3 px-4 text-gray-600 font-medium">Code</th>
              <th className="text-left py-3 px-4 text-gray-600 font-medium">Name</th>
              <th className="text-left py-3 px-4 text-gray-600 font-medium">Email(s)</th>
              <th className="text-left py-3 px-4 text-gray-600 font-medium">Status</th>
              <th className="text-right py-3 px-4 text-gray-600 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCustomers.map((customer) => (
              <tr key={customer.id} className="border-t hover:bg-gray-50">
                <td className="py-3 px-4 font-mono text-sm">{customer.code}</td>
                <td className="py-3 px-4">{customer.name}</td>
                <td className="py-3 px-4 text-sm text-gray-600 max-w-xs truncate">
                  {Array.isArray(customer.email_addresses)
                    ? customer.email_addresses.join(', ')
                    : customer.email_addresses || 'No email'}
                </td>
                <td className="py-3 px-4">
                  <span className={`px-2 py-1 rounded-full text-xs ${customer.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {customer.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="py-3 px-4 text-right">
                  <button onClick={() => openModal(customer)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg mr-1"><Edit2 size={18} /></button>
                  <button onClick={() => handleDelete(customer.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 size={18} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredCustomers.length === 0 && (
          <div className="text-center py-8 text-gray-500">No customers found</div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-4 sticky top-0 bg-white z-10 pb-2">
              <h2 className="text-xl font-bold">{editingCustomer ? 'Edit Customer' : 'Add Customer'}</h2>
              <button onClick={closeModal} className="p-2 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
            </div>

            {error && <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">{error}</div>}

            <form onSubmit={handleSubmit} className="space-y-4">

              {/* ============ BASIC ============ */}
              <h3 className={sectionHeaderClass}>Basic</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Name *</label>
                  <input type="text" required value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Code *</label>
                  <input type="text" required value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    className={`${inputClass} font-mono`} placeholder="e.g., LAN0001" />
                </div>
              </div>
              <div className="flex items-center">
                <input type="checkbox" id="active" checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="mr-2 h-4 w-4 text-[#1F2D3D]" />
                <label htmlFor="active" className="text-sm text-gray-700">Active</label>
              </div>

              {/* ============ DELIVERY NOTES ============ */}
              <h3 className={sectionHeaderClass}>Delivery Notes</h3>
              <div>
                <label className={labelClass}>Delivery Note Email Addresses</label>
                <textarea value={formData.email_addresses}
                  onChange={(e) => setFormData({ ...formData, email_addresses: e.target.value })}
                  className={inputClass} rows="2" placeholder="Comma-separated emails" />
                <p className={helpTextClass}>These receive delivery note notifications. Separate multiple addresses with commas.</p>
              </div>

              {/* ============ INVOICING — EMAILS ============ */}
              <h3 className={sectionHeaderClass}>Invoicing — Emails</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Invoice Email — TO</label>
                  <textarea value={formData.invoice_email_to_addresses}
                    onChange={(e) => setFormData({ ...formData, invoice_email_to_addresses: e.target.value })}
                    className={inputClass} rows="3" placeholder="One email per line" />
                  <p className={helpTextClass}>Primary recipients of the final invoice.</p>
                </div>
                <div>
                  <label className={labelClass}>Invoice Email — CC</label>
                  <textarea value={formData.invoice_email_cc_addresses}
                    onChange={(e) => setFormData({ ...formData, invoice_email_cc_addresses: e.target.value })}
                    className={inputClass} rows="3" placeholder="One email per line" />
                  <p className={helpTextClass}>Copied on the final invoice.</p>
                </div>
              </div>

              {/* ============ INVOICING — BILLING CONTACT ============ */}
              <h3 className={sectionHeaderClass}>Invoicing — Billing Contact</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Contact Name</label>
                  <input type="text" value={formData.billing_contact_name}
                    onChange={(e) => setFormData({ ...formData, billing_contact_name: e.target.value })}
                    className={inputClass} placeholder="e.g., Margaret Kaggwa" />
                </div>
                <div>
                  <label className={labelClass}>Contact Role</label>
                  <input type="text" value={formData.billing_contact_role}
                    onChange={(e) => setFormData({ ...formData, billing_contact_role: e.target.value })}
                    className={inputClass} placeholder="e.g., Laundry Manager" />
                </div>
              </div>
              <div>
                <label className={labelClass}>Billing Address</label>
                <textarea value={formData.billing_address_lines}
                  onChange={(e) => setFormData({ ...formData, billing_address_lines: e.target.value })}
                  className={inputClass} rows="4" placeholder={"The Landmark Hotel\n222 Marylebone Road\nLondon\nNW1 6JQ"} />
                <p className={helpTextClass}>One line per address line (shown exactly as entered on the invoice header).</p>
              </div>

              {/* ============ INVOICING — NUMBERING ============ */}
              <h3 className={sectionHeaderClass}>Invoicing — Numbering</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Location Ref</label>
                  <input type="number" value={formData.location_ref}
                    onChange={(e) => setFormData({ ...formData, location_ref: e.target.value })}
                    className={inputClass} placeholder="e.g., 32" min="0" max="99" />
                  <p className={helpTextClass}>2-digit location code (32 = Wates Way laundry, 64 = Stewarts Rd DC).</p>
                </div>
                <div>
                  <label className={labelClass}>Customer Ref</label>
                  <input type="number" value={formData.customer_ref}
                    onChange={(e) => setFormData({ ...formData, customer_ref: e.target.value })}
                    className={inputClass} placeholder="e.g., 13" min="0" max="99" />
                  <p className={helpTextClass}>2-digit customer code used in invoice numbers.</p>
                </div>
              </div>

              {/* ============ INVOICING — WORKFLOW ============ */}
              <h3 className={sectionHeaderClass}>Invoicing — Workflow</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Workflow</label>
                  <select value={formData.invoice_workflow}
                    onChange={(e) => setFormData({ ...formData, invoice_workflow: e.target.value })}
                    className={inputClass}>
                    <option value="direct">Direct (send invoice straight to accounts)</option>
                    <option value="po_first">PO First (send to PO recipient, await PO, then accounts)</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>PO Recipient Email</label>
                  <input type="email" value={formData.po_recipient_email}
                    onChange={(e) => setFormData({ ...formData, po_recipient_email: e.target.value })}
                    className={inputClass} placeholder="Only used when workflow is PO First"
                    disabled={formData.invoice_workflow !== 'po_first'} />
                  <p className={helpTextClass}>Stage-1 recipient for PO-first workflow (e.g. Laundry Manager).</p>
                </div>
              </div>

              {/* ============ INVOICING — RATES ============ */}
              <h3 className={sectionHeaderClass}>Invoicing — Rates</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Labelling Rate (£ ex VAT)</label>
                  <input type="number" step="0.01" value={formData.labelling_rate_ex_vat}
                    onChange={(e) => setFormData({ ...formData, labelling_rate_ex_vat: e.target.value })}
                    className={inputClass} placeholder="e.g., 0.20" min="0" />
                  <p className={helpTextClass}>Flat per-unit labelling charge. Leave blank if customer isn't charged for labelling.</p>
                </div>
              </div>

              {/* ============ INVOICING — TEMPLATE OVERRIDES (Step 3c-ii) ============ */}
              <h3 className={sectionHeaderClass}>Invoicing — Template Overrides</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Rate decimal places</label>
                  <input type="number" min="0" max="4" step="1" value={formData.rate_decimal_places}
                    onChange={(e) => setFormData({ ...formData, rate_decimal_places: e.target.value })}
                    className={inputClass} />
                  <p className={helpTextClass}>2 for most customers. Ritz uses 3 (£0.295). Affects how rates are formatted on the PDF.</p>
                </div>
                <div>
                  <label className={labelClass}>Numbered tablecloth column</label>
                  <div className="flex items-center mt-2">
                    <input type="checkbox" id="show_numbered_tablecloth_column"
                      checked={!!formData.show_numbered_tablecloth_column}
                      onChange={(e) => setFormData({ ...formData, show_numbered_tablecloth_column: e.target.checked })}
                      className="w-4 h-4 mr-2" />
                    <label htmlFor="show_numbered_tablecloth_column" className="text-sm text-gray-700">Show "No" column on tablecloths</label>
                  </div>
                  <p className={helpTextClass}>Tick for O&amp;C F&amp;B only. Adds a numbered column 1-8 next to the 8 standard tablecloth sizes.</p>
                </div>
              </div>

              {/* ============ ACTIONS ============ */}
              <div className="flex justify-end gap-3 pt-4 border-t mt-6">
                <button type="button" onClick={closeModal} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-[#1F2D3D] text-white rounded-lg hover:bg-[#1A2332]">
                  {editingCustomer ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Customers;
